"use client";

import {useEffect, useLayoutEffect, useRef, useState} from "react";
import {EVENT_COPY} from "../content";
import {prefersReducedMotion} from "./useMission";
import css from "../mjbc-2026.module.scss";

/**
 * Served from R2 rather than public/, the same way the Singular hero clips are
 * (see (site)/singular/page.tsx). The encodes plus their posters and fog plates
 * are several MB of binary that never changes once cut, which does not belong
 * in git.
 *
 * Upload with:
 *   npx tsx scripts/upload-r2-media.ts video <files...>
 *
 * `-v2` is the cut WITH SOUND — same footage frame for frame as the silent one it
 * replaces, so the posters and the fog plates cut from it still match. R2 serves
 * these `immutable`, so a new mix has to arrive under a new key rather than
 * overwrite the old one; bump the suffix again if a third ever lands.
 *
 * The agency masters are ~32MB apiece at 16Mb/s. What ships is re-encoded from
 * them to the delivery sizes at the bitrates the silent cut used, so the only
 * thing the sound adds to a show-floor download is its own 128kb/s:
 *
 *   ffmpeg -i "<master>.mp4" -vf scale=1600:900:flags=lanczos \
 *     -c:v libx264 -preset slow -b:v 1364k -pass 1|2 -pix_fmt yuv420p -profile:v high \
 *     -af "loudnorm=I=-16:TP=-1.5:LRA=11:measured_I=-8.49:measured_TP=1.06:\
 *          measured_LRA=4.10:measured_thresh=-18.65:linear=true" \
 *     -c:a aac -b:a 128k -ac 2 -ar 48000 -movflags +faststart <out>.mp4
 *
 * Portrait is the same line at scale=720:1280 and -b:v 925k.
 *
 * THE LOUDNESS PASS IS NOT OPTIONAL. The masters are mixed at -8.5 LUFS with
 * true peaks at +1 dBFS — around 8dB hotter than anything else a browser plays,
 * and clipping. This lands them at -16 LUFS / -1.5 dBTP, the streaming norm, as
 * a straight gain change (`linear=true`) so the mix itself is untouched. It has
 * to happen in the file: iOS ignores `video.volume` outright, so there is no
 * turning it down from JS afterwards.
 */
const MEDIA = 'https://images.active710.com/video/mjbc-2026';

/** The clip itself, versioned; posters and fog plates are not. */
const LAUNCH_CUT = 'v2';

/**
 * Backstop for `onSettled`. The clip runs 16s; if autoplay is refused or the
 * file never loads, `ended` never fires and the frame-9 backdrop would never
 * arrive, so settle anyway shortly after it would have finished.
 */
const MAX_LAUNCH_MS = 18_000;

/**
 * How long before the last frame the dissolve starts. PORTRAIT ONLY.
 *
 * Neither clip ends on a still — there is exhaust drifting across the frame
 * right up to frame 385 — so playback stopping is a visible event: the smoke
 * simply halts. Waiting for `ended` to begin the fade meant that halt happened
 * at full opacity and a frozen frame then dissolved, which reads as a video
 * finishing rather than as smoke clearing.
 *
 * It is only a problem in portrait. Measured over the closing second, the 9x16
 * cut carries about a third more motion per frame than the 16x9 one (mean
 * inter-frame delta ~2.2 against ~1.5), across a plume that fills the screen
 * instead of sitting in the middle of a wide frame. Landscape gets away with
 * stopping; portrait does not.
 *
 * So portrait fades over the tail of the playing clip: 1800 against the
 * stylesheet's 1400ms transition puts the video at zero roughly 400ms before it
 * stops, and it is moving smoke that dissolves rather than a still of it.
 * Landscape is deliberately left alone — it holds its trailing exhaust a beat
 * longer, which is the desktop timing as drawn and as signed off.
 *
 * The margin also covers `timeupdate`, which browsers only fire about four
 * times a second.
 */
const DISSOLVE_LEAD_MS = 1800;

/**
 * Portrait and landscape are separate encodes rather than one file cropped,
 * because the design frames the shot differently at 393px than at 1440px.
 * Chosen once in JS: `<source media>` inside `<video>` is unreliable across
 * browsers and is not re-evaluated on resize, and rendering both elements would
 * download both files.
 *
 * These only ever mount after the boot sequence, so they are never part of the
 * server-rendered HTML and the lazy initialiser cannot cause a hydration
 * mismatch.
 */
export type LaunchVariant = '9x16' | '16x9';

function useLaunchVariant(): LaunchVariant {
    const [portrait] = useState(
        () => typeof window !== 'undefined' && window.matchMedia('(max-width: 900px)').matches
    );
    return portrait ? '9x16' : '16x9';
}

/**
 * Built here rather than in each caller so the preloader can never warm one cut
 * while the player asks for another — which would show as the bar filling and
 * the rocket then downloading itself all over again.
 */
export const launchClip = (variant: LaunchVariant): string =>
    `${MEDIA}-rocket-launch-${variant}-${LAUNCH_CUT}.mp4`;

/** Cut from the footage, which the new mix did not change. Not versioned. */
export const launchPoster = (variant: LaunchVariant): string =>
    `${MEDIA}-rocket-launch-${variant}.jpg`;

interface LaunchVideoProps {
    /** Drives the dissolve once the launch is spent. */
    settled?: boolean;
    /** Fires when the launch is over, so the fog can be left behind. */
    onSettled?: () => void;
}

/**
 * Frames 6-8: the rocket leaving, composited OVER the page background rather
 * than replacing it. In the design these are three frames of the same artwork
 * panned upward (desktop y -106 -> -235 -> -239) with the invite reading on the
 * backdrop behind it — the rocket departs, the information stays.
 *
 * BLEND MODE IS A STAND-IN. The intended asset is a clip with an alpha channel;
 * this one is opaque, so `screen` is used to drop its near-black night sky and
 * let the circuit backdrop through. It reads correctly because the footage is
 * dark, but the exhaust plume stays slightly milky where the real matte would
 * be clean. When the alpha export lands (VP9/WebM with alpha, or HEVC with
 * alpha), drop `mix-blend-mode` from .launchVideo and nothing else changes.
 */
export default function LaunchVideo({settled, onSettled}: LaunchVideoProps) {
    const variant = useLaunchVariant();
    const [reduced] = useState(prefersReducedMotion);
    // Portrait only, and set from the clip's own clock rather than a timer
    // started at mount, so a backgrounded tab — which pauses playback on mobile
    // — resumes with the dissolve still sitting on the last 1800ms of footage.
    const [spent, setSpent] = useState(false);
    // What the element is actually doing, not what we asked for: a browser that
    // refuses sound sets this back to true from the catch below.
    const [muted, setMuted] = useState(false);
    const fadesEarly = variant === '9x16';

    const clip = useRef<HTMLVideoElement>(null);
    const settle = useRef(onSettled);
    settle.current = onSettled;

    useEffect(() => {
        const timer = setTimeout(() => settle.current?.(), MAX_LAUNCH_MS);
        return () => clearTimeout(timer);
    }, []);

    /*
     * Started by hand rather than with `autoplay`, and unmuted.
     *
     * The launch only ever mounts from the nameplate's CTA, so the click that
     * gets us here IS the gesture that lets a browser play sound — but only if
     * we spend it. useLayoutEffect runs inside the commit React does
     * synchronously for a click, which keeps the play() call in the same task as
     * the click itself; a passive effect is scheduled after it, and Safari has
     * stopped counting the gesture by then.
     *
     * Refusal is still survivable. Anything that rejects — an iOS low-power
     * mode, a tab muted at the OS, an autoplay policy we did not anticipate —
     * falls back to the muted launch the page has always had, with the toggle
     * below left showing that sound is available.
     */
    useLayoutEffect(() => {
        const video = clip.current;
        if (video === null) return;

        video.muted = false;
        video.play().catch(() => {
            video.muted = true;
            setMuted(true);
            // If it will not play at all, do not strand the sequence on a rocket
            // that never flies — the backstop above would eventually settle it,
            // but there is no reason to make anyone wait out 18 seconds.
            video.play().catch(() => settle.current?.());
        });
    }, []);

    // Reduced motion gets the poster frame, not a moving rocket.
    if (reduced) {
        return (
            <div
                className={css.launchStill}
                style={{backgroundImage: `url(${launchPoster(variant)})`}}
                data-settled={settled ? '' : undefined}
                aria-hidden="true"
            />
        );
    }

    const toggleSound = () => {
        const video = clip.current;
        if (video === null) return;

        video.muted = !video.muted;
        setMuted(video.muted);
        // Unmuting is itself a gesture, so this is the second chance for a
        // launch that was refused sound and never started at all.
        if (video.paused) void video.play().catch(() => undefined);
    };

    return (
        <>
            <video
                ref={clip}
                className={css.launchVideo}
                data-settled={settled || spent ? '' : undefined}
                src={launchClip(variant)}
                poster={launchPoster(variant)}
                onTimeUpdate={!fadesEarly ? undefined : event => {
                    const video = event.currentTarget;
                    const left = (video.duration - video.currentTime) * 1000;

                    // duration is NaN until metadata lands, and NaN fails this
                    // the same way a clip with time left does.
                    if (left <= DISSOLVE_LEAD_MS) setSpent(true);
                }}
                onEnded={() => settle.current?.()}
                onError={() => settle.current?.()}
                playsInline
                preload="auto"
                aria-hidden="true"
            />

            <SoundToggle muted={muted} onToggle={toggleSound} gone={settled || spent}/>
        </>
    );
}

/**
 * The one control on the launch.
 *
 * It is not decoration: the mix runs for sixteen seconds over a screen nobody
 * asked to be loud, so WCAG 1.4.2 wants a way to stop it, and on a browser that
 * refused unmuted playback this is also the only thing that offers the sound
 * back. That second job is why it carries a label while muted and shrinks to
 * the glyph once sound is on — silent is the state that needs explaining.
 *
 * It leaves with the clip rather than outliving it: `gone` is the same flag that
 * dissolves the video, so the corner is empty by the time the briefing has the
 * screen.
 */
function SoundToggle({muted, onToggle, gone}: {muted: boolean; onToggle: () => void; gone?: boolean}) {
    return (
        <button
            type="button"
            className={css.soundToggle}
            data-settled={gone ? '' : undefined}
            // It fades rather than unmounting, so it is still in the document
            // over the briefing — and a control nobody can see must not still be
            // in the tab order or read out. `inert` is the half CSS cannot do.
            inert={gone}
            onClick={onToggle}
            aria-label={muted ? EVENT_COPY.soundOn : EVENT_COPY.soundOff}
        >
            <SpeakerIcon muted={muted}/>
            {muted ? <span>{EVENT_COPY.soundOn}</span> : null}
        </button>
    );
}

/**
 * Inline rather than another file in /images: it is two paths, it has to take
 * the button's own colour on hover and focus, and the launch is the one screen
 * where a second network request would land in the middle of the sequence.
 */
function SpeakerIcon({muted}: {muted: boolean}) {
    return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
            <path d="M11 4.5 6.5 8.5H3v7h3.5L11 19.5z" strokeLinejoin="round"/>
            {muted
                ? <path d="m15.5 9.5 5 5m0-5-5 5" strokeLinecap="round"/>
                : <path d="M15 9a4 4 0 0 1 0 6m2.8-8.8a8 8 0 0 1 0 11.6" strokeLinecap="round"/>}
        </svg>
    );
}
